/**
 * Side view (Cole, 2026-10-21, after a player's post: putting on a projector
 * while the match plays): LagKing in its own window on a third of the screen,
 * so the game can keep the other two-thirds. The page cannot shrink another
 * site's video (Netflix, YouTube TV and the like refuse to be embedded), so
 * the game stays in its own window; this module sizes and places ours.
 * Players pick the side. Pure: no DOM, so node tests pin the geometry.
 */

import { appShellFrom } from './appShell';

export type SideViewSide = 'left' | 'right';

/** The query flag a side window carries: `?side=left` or `?side=right`. */
export const SIDE_VIEW_PARAM = 'side';
/** The window name, so a second click reuses the same side window. */
export const SIDE_VIEW_WINDOW = 'lagking-side-view';
/** The viewer's last pick, remembered per browser. */
export const SIDE_VIEW_STORAGE_KEY = 'lagking.tv.sideView';

/** The one side-view test every caller uses (entry point, layout, labels,
 *  sound). Never inside a TV app shell: a shell is full screen on a TV, so a
 *  stray `side` flag there is ignored everywhere at once (peer review F06). */
export function sideViewFrom(search: string): SideViewSide | null {
  if (appShellFrom(search)) return null;
  const side = new URLSearchParams(search).get(SIDE_VIEW_PARAM);
  return side === 'left' || side === 'right' ? side : null;
}

export interface ScreenArea {
  availLeft: number;
  availTop: number;
  availWidth: number;
  availHeight: number;
}

export interface WindowFrame {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A third of the screen's usable width, full height, on the chosen side. */
export function sideViewFrame(screen: ScreenArea, side: SideViewSide): WindowFrame {
  const width = Math.max(360, Math.round(screen.availWidth / 3));
  const left = side === 'left' ? screen.availLeft : screen.availLeft + screen.availWidth - width;
  return { left: Math.round(left), top: Math.round(screen.availTop), width, height: Math.round(screen.availHeight) };
}

/** window.open's feature string for the side window. */
export function sideViewFeatures(frame: WindowFrame): string {
  return `popup,width=${frame.width},height=${frame.height},left=${frame.left},top=${frame.top}`;
}

/** The side window's address: this page with `side` set (any demo or other
 *  flag kept), and `watch` when Watch + putt opened it. */
export function sideViewUrl(href: string, side: SideViewSide, watch = false): string {
  const url = new URL(href);
  url.searchParams.set(SIDE_VIEW_PARAM, side);
  if (watch) url.searchParams.set(WATCH_PARAM, '1');
  else url.searchParams.delete(WATCH_PARAM);
  return url.toString();
}

/** The full-window address: the same page without `side` or `watch`. */
export function fullViewUrl(href: string): string {
  const url = new URL(href);
  url.searchParams.delete(SIDE_VIEW_PARAM);
  url.searchParams.delete(WATCH_PARAM);
  return url.toString();
}

/** The query flag LagKing's side window carries when Watch + putt opened it:
 *  it shows the game picker on load. */
export const WATCH_PARAM = 'watch';
/** The viewer's last game service, remembered per browser. */
export const GAME_VIEW_STORAGE_KEY = 'lagking.tv.gameView';

/** The other two-thirds of the screen: the game's window beside LagKing's. */
export function gameViewFrame(screen: ScreenArea, lagkingSide: SideViewSide): WindowFrame {
  const side = sideViewFrame(screen, lagkingSide);
  const width = Math.round(screen.availWidth - side.width);
  const left = lagkingSide === 'left' ? screen.availLeft + side.width : screen.availLeft;
  return { left: Math.round(left), top: Math.round(screen.availTop), width, height: Math.round(screen.availHeight) };
}

export interface GameService {
  id: string;
  label: string;
  url: string;
}

/** Where the game usually is. Any other http(s) link works too (gameViewUrl). */
export const GAME_SERVICES: readonly GameService[] = [
  { id: 'youtubetv', label: 'YouTube TV', url: 'https://tv.youtube.com/' },
  { id: 'youtube', label: 'YouTube', url: 'https://www.youtube.com/' },
  { id: 'espn', label: 'ESPN', url: 'https://www.espn.com/watch/' },
  { id: 'peacock', label: 'Peacock', url: 'https://www.peacocktv.com/' },
  { id: 'netflix', label: 'Netflix', url: 'https://www.netflix.com/' },
  { id: 'prime', label: 'Prime Video', url: 'https://www.primevideo.com/' },
  { id: 'max', label: 'Max', url: 'https://play.max.com/' },
  { id: 'paramount', label: 'Paramount+', url: 'https://www.paramountplus.com/' },
];

/** A typed link, checked before it opens: http(s) only, a scheme added when
 *  missing, a dotted host, and no user name or password (a "name@" prefix can
 *  make a link look like one site while it opens another). The result is the
 *  canonical address, so the page can show its real host. Null when unusable. */
export function gameViewUrl(raw: string): string | null {
  const text = raw.trim();
  if (!text) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(text) ? text : `https://${text}`;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (url.username || url.password) return null;
    if (!url.hostname.includes('.')) return null;
    return url.toString();
  } catch {
    return null;
  }
}

/** A window's outer frame as the browser reports it (screenX/Y, outerWidth/Height). */
export interface MeasuredFrame {
  screenX: number;
  screenY: number;
  outerWidth: number;
  outerHeight: number;
}

/** Whether a window landed where it was asked to go. A browser may clamp,
 *  ignore or tab a placement without saying so, so the page measures before
 *  it says "arranged" (peer review F08). Tolerance covers rounding and a
 *  title bar the browser keeps. */
export function frameLanded(measured: MeasuredFrame, frame: WindowFrame, tolerance = 40): boolean {
  return Math.abs(measured.screenX - frame.left) <= tolerance
    && Math.abs(measured.screenY - frame.top) <= tolerance
    && Math.abs(measured.outerWidth - frame.width) <= tolerance
    && Math.abs(measured.outerHeight - frame.height) <= tolerance;
}

export type ArrangePlatform = 'windows' | 'mac' | 'other';

export function arrangePlatform(userAgent: string): ArrangePlatform {
  if (/Windows/i.test(userAgent)) return 'windows';
  if (/Macintosh|Mac OS X/i.test(userAgent)) return 'mac';
  return 'other';
}

/** One exact gesture per system for putting the game beside LagKing when the
 *  browser would not place it. Windows Snap's layouts depend on the display,
 *  and macOS tiling has halves and quarters, not thirds, so neither promises
 *  an exact third. */
export function arrangeHelp(platform: ArrangePlatform): string {
  if (platform === 'windows') {
    return 'Click the game window, press Windows key + Z, and pick a layout with a narrow side for LagKing. Then choose LagKing in Snap Assist.';
  }
  if (platform === 'mac') {
    return 'Drag the game window by its title bar and hold Option over the side away from LagKing to tile it, or drag its edge until it meets LagKing.';
  }
  return 'Drag the game window beside LagKing and resize its edge until the two meet.';
}
